import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';
import { X } from 'lucide-react';
import { isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import InvestmentRateSelect from './InvestmentRateSelect';

const PANEL_WIDTH = 304;
const PANEL_HEIGHT = 240;
const VIEWPORT_GAP = 12;

type Position = { left: number; top: number };

function activeTableRow(editor: Editor): HTMLTableRowElement | null {
  try {
    const dom = editor.view.domAtPos(editor.state.selection.from).node;
    const element = dom instanceof Element ? dom : dom.parentElement;
    return element?.closest('tr') ?? null;
  } catch {
    return null;
  }
}

function panelPosition(row: HTMLTableRowElement): Position {
  const rect = row.getBoundingClientRect();
  const left = Math.max(
    VIEWPORT_GAP,
    Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - VIEWPORT_GAP),
  );
  const below = rect.bottom + 8;
  const top =
    below + PANEL_HEIGHT <= window.innerHeight - VIEWPORT_GAP
      ? below
      : Math.max(VIEWPORT_GAP, rect.top - PANEL_HEIGHT - 8);
  return { left: Math.round(left), top: Math.round(top) };
}

function numberInputValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^-?\d+(?:[.,]\d+)?$/.test(trimmed)) {
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? String(parsed) : '';
  }
  if (!isCurrencyAmount(trimmed)) return '';
  const parsed = parseCurrencyAmount(trimmed);
  return Number.isFinite(parsed) ? String(parsed) : '';
}

export default function InvestmentRowInlineEditor({ editor }: { editor: Editor }) {
  const group = useInvestmentInspectorStore((state) => state.group);
  const phase = useInvestmentInspectorStore((state) => state.phase);
  const row = phase?.row ?? null;
  const rowKey = row && phase ? `${phase.tableId}:${row.index}` : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [hours, setHours] = useState('');

  const visible = Boolean(
    row && !row.investmentProduct &&
      phase?.pricingType === 'hourly' &&
      phase.hasHourColumns &&
      !(row.showHours ?? phase.showHourColumns) &&
      rowKey !== dismissedKey,
  );

  useEffect(() => {
    setHours(numberInputValue(row?.cells[2] ?? ''));
  }, [rowKey, row?.cells]);

  useEffect(() => {
    if (rowKey !== dismissedKey) return;
    const onSelection = () => {
      const nextRow = activeTableRow(editor);
      if (!nextRow) setDismissedKey(null);
    };
    editor.on('selectionUpdate', onSelection);
    return () => {
      editor.off('selectionUpdate', onSelection);
    };
  }, [dismissedKey, editor, rowKey]);

  const updatePosition = useCallback(() => {
    if (!visible) {
      setPosition(null);
      return;
    }
    const tableRow = activeTableRow(editor);
    setPosition(tableRow ? panelPosition(tableRow) : null);
  }, [editor, visible]);

  useLayoutEffect(() => {
    updatePosition();
    if (!visible) return;
    const update = () => updatePosition();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    editor.on('selectionUpdate', update);
    editor.on('transaction', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
    };
  }, [editor, updatePosition, visible]);

  const currency = group?.currency ?? 'EUR';
  const total = row?.cells[3] || '—';
  const title = useMemo(() => row?.title.trim() || `Regel ${row?.index ?? ''}`, [row]);

  const handleHistoryShortcut = (event: KeyboardEvent<HTMLInputElement>): boolean => {
    const modifier = event.metaKey || event.ctrlKey;
    if (!modifier || event.key.toLowerCase() !== 'z') return false;
    event.preventDefault();
    event.stopPropagation();
    if (event.shiftKey) editor.commands.redo();
    else editor.commands.undo();
    const nextRow = useInvestmentInspectorStore.getState().phase?.row;
    setHours(numberInputValue(nextRow?.cells[2] ?? ''));
    return true;
  };

  if (!visible || !row || !phase || !position) return null;

  const commitHours = () => {
    const parsed = Number(hours);
    if (hours !== '' && (!Number.isFinite(parsed) || parsed < 0)) {
      setHours(numberInputValue(row.cells[2] ?? ''));
      return;
    }
    const next = hours === '' ? '' : String(parsed);
    if (next !== (row.cells[2] ?? '')) phase.setRowCell(row.index, 2, next);
  };

  const updateHours = (input: HTMLInputElement) => {
    const next = input.value;
    setHours(next);
    if (next === '') {
      phase.setRowCell(row.index, 2, '');
      return;
    }
    if (Number.isFinite(input.valueAsNumber) && input.valueAsNumber >= 0) {
      phase.setRowCell(row.index, 2, String(input.valueAsNumber));
    }
  };

  return createPortal(
    <section
      className="fixed z-[80] w-[304px] rounded-xl border border-slate-200 bg-white p-3 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
      style={position}
      aria-label={`Interne calculatie voor ${title}`}
      data-investment-inspector="true"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-slate-900">{title}</p>
          <p className="text-[11px] text-slate-500">Alleen intern zichtbaar</p>
        </div>
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => setDismissedKey(rowKey)}
          aria-label="Interne calculatie sluiten"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <InvestmentRateSelect
          rateKey={row.rateKey}
          amount={row.cells[1] ?? ''}
          currency={currency}
          onSelect={(key, cents) => phase.setRowRate?.(row.index, key, cents)}
          onCustomChange={(value) => phase.setRowCell(row.index, 1, value)}
          onKeyDown={handleHistoryShortcut}
        />
        <label className="min-w-0">
          <span className="mb-1 block text-[11px] font-semibold text-slate-700">Uren</span>
          <input
            type="number"
            min="0"
            step="0.25"
            value={hours}
            onChange={(event) => updateHours(event.currentTarget)}
            onBlur={commitHours}
            onKeyDown={(event) => {
              if (handleHistoryShortcut(event)) return;
              if (event.key === 'Enter') event.currentTarget.blur();
              if (event.key === 'Escape') {
                editor.commands.undo();
              }
            }}
            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[14px] text-slate-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15"
            aria-label="Interne uren"
          />
        </label>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-slate-100 pt-2">
        <span className="text-[12px] text-slate-500">Regeltotaal</span>
        <output className="text-[14px] font-bold tabular-nums text-slate-900" aria-live="polite">
          {total}
        </output>
      </div>
    </section>,
    document.body,
  );
}
